import { RiAlertFill, RiErrorWarningLine, RiLinksLine, RiLinkUnlinkM, RiRefreshLine } from '@remixicon/react';
import moment from 'moment';
import { useEffect, useMemo, useRef, useState } from 'react';
import ConfirmModal from 'src/components/containers/modals/ConfirmModal';
import { AgButton, AgText } from 'src/components/ui';
import { AgTab, AgTabs } from 'src/components/ui/AgTabs';
import { useSettingsAccountingSearch } from 'src/routing/routeSearch';
import AccountMappingsTable from './AccountMappingsTable';
import quickbooksLogo from './img/quickbooks-logo.svg';
import { consumeInitialQboCallbackSearch } from './initialQboCallbackSearch';
import QBOPreferencesTab from './QBOPreferencesTab';
import {
  useBeginQuickbooksConnection,
  useCompleteQuickbooksConnection,
  useDisconnectQuickbooksConnection,
  useGetQuickbooksIntegrationId,
  useThirdAndFirstPartyEntities,
  useValidateQuickbooksConnection,
} from './quickbooks.api';

export enum QBOSettingsSubTabs {
  ACCOUNT_MAPPINGS = 'account-mappings',
  PREFERENCES = 'preferences',
}

export default function QBOSettingsTab() {
  const integration = useGetQuickbooksIntegrationId();
  const { data: connection, loading: loadingConnection } = useBeginQuickbooksConnection();
  const {
    data: validationData,
    loading: loadingValidation,
    refetch: refetchValidation,
  } = useValidateQuickbooksConnection();
  const [completeQuickbooksConnection] = useCompleteQuickbooksConnection();
  const [disconnectQuickbooksConnection, { loading: disconnecting }] = useDisconnectQuickbooksConnection();
  const { data: entitiesData } = useThirdAndFirstPartyEntities();
  const hasHandledRedirect = useRef(false);
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);

  const hasUnmapped = useMemo(
    () => !!entitiesData?.thirdPartyIntegrationEntityLinks?.entities?.some((e: { account?: unknown }) => !e.account),
    [entitiesData]
  );

  const { code } = useSettingsAccountingSearch();

  // after authorizing access in QBO, we're redirected back to this page with ?code=&realmId=
  useEffect(() => {
    if (hasHandledRedirect.current || !code) return;
    // Flip the guard synchronously so StrictMode's double-invoke of this effect
    // can't fire a second mutation with the same (single-use) OAuth code.
    hasHandledRedirect.current = true;

    // Never the router's view of the query: TanStack JSON-parses realmId (losing digits) and re-quotes it.
    const qboCallbackString = consumeInitialQboCallbackSearch() ?? window.location.search.replace(/^\?/, '');

    // Strip OAuth params immediately so a refresh or remount can't retry the
    // now-consumed code. Preserve the rest of the URL (active tab, etc.).
    const url = new URL(window.location.href);
    ['code', 'realmId', 'state'].forEach((k) => url.searchParams.delete(k));
    window.history.replaceState(window.history.state, '', url.pathname + url.search);

    completeQuickbooksConnection({ variables: { redirectQueryString: qboCallbackString } }).then(() =>
      refetchValidation()
    );
  }, [code, completeQuickbooksConnection, refetchValidation]);

  const isConnected = validationData?.validateQuickbooksConnection?.isValid ?? false;
  const validationIntegration = validationData?.validateQuickbooksConnection?.integration;

  const hasAuthError = useMemo(() => {
    if (loadingValidation) return false;
    if (isConnected) return false;
    return !!(integration && !isConnected);
  }, [loadingValidation, isConnected, integration]);

  const lastSyncLabel = useMemo(() => {
    const ts = validationIntegration?.updatedAt ?? null;
    return ts ? moment(ts).format('DD.MM.YYYY, HH:mm:ss') : null;
  }, [validationIntegration?.updatedAt]);

  const handleConnect = () => {
    if (!connection?.beginQuickbooksConnection?.authorizationUri) return;
    window.location.replace(connection.beginQuickbooksConnection.authorizationUri);
  };

  const handleConfirmDisconnect = async () => {
    await disconnectQuickbooksConnection();
    setShowDisconnectConfirm(false);
  };

  return (
    <div className="flex flex-col gap-6">
      {hasAuthError && (
        <div className="flex gap-3 rounded-[12px] border border-red-border-light bg-red-bg p-4 text-red-text">
          <RiErrorWarningLine size={20} className="shrink-0" />
          <div className="flex flex-col gap-1">
            <AgText size="14" weight="semibold" color="inherit">
              Connection interrupted
            </AgText>
            <AgText size="14" color="inherit">
              Agriful lost access to your QuickBooks account.
              {lastSyncLabel ? ` Last sync: ${lastSyncLabel}` : ''}
            </AgText>
          </div>
        </div>
      )}

      {/* Connection header card */}
      <div className="card p-6 flex items-center gap-10">
        <div className="flex flex-col gap-3 max-w-[400px]">
          <div className="flex items-center gap-2">
            <img src={quickbooksLogo} alt="QuickBooks Online" className="h-6 w-6 shrink-0" />
            <AgText size="16" weight="medium">
              QuickBooks Online
            </AgText>
            <ConnectionBadge loading={loadingValidation} isConnected={isConnected} />
          </div>
          <AgText size="14" className="text-neutral-alpha-9">
            Enable automatic data exchange for invoices, payments, and accounts to streamline your bookkeeping and stay
            in sync with your financial records.
          </AgText>
        </div>

        <div className="flex items-center gap-3 shrink-0">
          {!integration && (
            <AgButton variant="white" onPress={handleConnect} isDisabled={loadingConnection}>
              <RiLinksLine size={16} />
              Connect to QBO
            </AgButton>
          )}
          {integration && (
            <AgButton
              variant="primary"
              className="rounded-[10px]"
              onPress={handleConnect}
              isDisabled={loadingConnection}
            >
              <RiRefreshLine size={16} />
              Reconnect
            </AgButton>
          )}
          {integration && isConnected && (
            <AgButton
              variant="destructive-secondary"
              onPress={() => setShowDisconnectConfirm(true)}
              isDisabled={disconnecting}
            >
              <RiLinkUnlinkM size={16} />
              Disconnect
            </AgButton>
          )}
        </div>
      </div>

      {/* Sub-tabs — hidden when not connected */}
      {isConnected && (
        <AgTabs queryParam="qboTab">
          <AgTab
            id={QBOSettingsSubTabs.ACCOUNT_MAPPINGS}
            label="Account mappings"
            iconAfter={hasUnmapped ? <RiAlertFill size={16} className="text-orange-icon" /> : undefined}
          >
            <AccountMappingsTable
              onReconnect={handleConnect}
              canConnect={!loadingConnection && !!connection?.beginQuickbooksConnection?.authorizationUri}
            />
          </AgTab>
          <AgTab id={QBOSettingsSubTabs.PREFERENCES} label="Preferences">
            <QBOPreferencesTab />
          </AgTab>
        </AgTabs>
      )}

      <ConfirmModal
        open={showDisconnectConfirm}
        title="Disconnect QuickBooks Online?"
        prompt="Automatic syncing of invoices, payments, and accounts will stop until you reconnect. Your existing account mappings will be preserved."
        action="Disconnect"
        destructive
        disableConfirm={disconnecting}
        handleClose={() => setShowDisconnectConfirm(false)}
        handleConfirm={handleConfirmDisconnect}
      />
    </div>
  );
}

function ConnectionBadge({ loading, isConnected }: { loading: boolean; isConnected: boolean }) {
  if (loading) return <span className="badge">Checking...</span>;
  if (isConnected) {
    return (
      <span className="badge badge-green inline-flex items-center gap-1.5">
        <RiLinksLine size={14} />
        Connected
      </span>
    );
  }
  return (
    <span className="badge badge-red inline-flex items-center gap-1.5">
      <RiLinkUnlinkM size={14} />
      Disconnected
    </span>
  );
}
